import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Static translations for ErrorBoundary.
 * These are embedded directly because i18n may have failed when this component renders.
 */
const errorTranslations: Record<string, { title: string; message: string; retry: string }> = {
  en: {
    title: 'Something went wrong',
    message: 'We encountered an error loading the page. Please try again.',
    retry: 'Try Again',
  },
  he: {
    title: 'משהו השתבש',
    message: 'נתקלנו בשגיאה בטעינת הדף. אנא נסו שוב.',
    retry: 'נסה שוב',
  },
  es: {
    title: 'Algo sali\u00F3 mal',
    message: 'Encontramos un error al cargar la p\u00E1gina. Por favor, int\u00E9ntalo de nuevo.',
    retry: 'Intentar de nuevo',
  },
  fr: {
    title: "Une erreur s'est produite",
    message: 'Nous avons rencontr\u00E9 une erreur lors du chargement de la page. Veuillez r\u00E9essayer.',
    retry: 'R\u00E9essayer',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    message: 'Beim Laden der Seite ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    retry: 'Erneut versuchen',
  },
  ar: {
    title: '\u062D\u062F\u062B \u062E\u0637\u0623 \u0645\u0627',
    message:
      '\u0648\u0627\u062C\u0647\u0646\u0627 \u062E\u0637\u0623 \u0641\u064A \u062A\u062D\u0645\u064A\u0644 \u0627\u0644\u0635\u0641\u062D\u0629. \u064A\u0631\u062C\u0649 \u0627\u0644\u0645\u062D\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649.',
    retry: '\u062D\u0627\u0648\u0644 \u0645\u0631\u0629 \u0623\u062E\u0631\u0649',
  },
  'pt-BR': {
    title: 'Algo deu errado',
    message: 'Encontramos um erro ao carregar a p\u00E1gina. Por favor, tente novamente.',
    retry: 'Tentar novamente',
  },
  it: {
    title: 'Qualcosa \u00E8 andato storto',
    message: 'Si \u00E8 verificato un errore durante il caricamento della pagina. Riprova.',
    retry: 'Riprova',
  },
  ja: {
    title: '問題が発生しました',
    message: 'ページの読み込み中にエラーが発生しました。もう一度お試しください。',
    retry: '再試行',
  },
};

/** RTL languages that need dir="rtl" */
const rtlLanguages = ['he', 'ar'];

/**
 * Detect user's language from localStorage or navigator.
 * Falls back to English if no match found.
 */
function detectLanguage(): string {
  try {
    // First check localStorage (i18next stores language there)
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      // Handle full locale codes like 'pt-BR' or 'en-US'
      if (storedLang in errorTranslations) {
        return storedLang;
      }
      // Try base language (e.g., 'en' from 'en-US')
      const baseLang = storedLang.split('-')[0] ?? '';
      if (baseLang && baseLang in errorTranslations) {
        return baseLang;
      }
    }

    // Fall back to navigator.language
    const browserLang = navigator.language;
    if (browserLang) {
      if (browserLang in errorTranslations) {
        return browserLang;
      }
      const baseLang = browserLang.split('-')[0] ?? '';
      if (baseLang && baseLang in errorTranslations) {
        return baseLang;
      }
    }
  } catch {
    // localStorage might not be available
  }

  return 'en';
}

/**
 * Error boundary to catch and handle React errors gracefully.
 * Used to prevent the entire app from crashing if translations fail to load
 * or other unexpected errors occur.
 *
 * This component uses embedded translations since i18n may not be available
 * when an error occurs.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const lang = detectLanguage();
      const translations = errorTranslations[lang] ??
        errorTranslations.en ?? {
          title: 'Something went wrong',
          message: 'We encountered an error loading the page. Please try again.',
          retry: 'Try Again',
        };
      const isRtl = rtlLanguages.includes(lang);

      return (
        <div className="flex min-h-screen items-center justify-center bg-canvas p-6" dir={isRtl ? 'rtl' : 'ltr'}>
          <div className="max-w-sm text-center">
            <div className="mb-4 text-4xl">⚠️</div>
            <h1 className="mb-2 text-xl font-semibold text-foreground">{translations.title}</h1>
            <p className="mb-4 text-[15px] text-muted-foreground">{translations.message}</p>
            <button
              onClick={this.handleRetry}
              className="inline-flex min-h-[48px] items-center justify-center rounded-lg bg-primary px-4 py-3 text-[15px] font-medium text-white hover:bg-primary/90 transition-colors"
            >
              {translations.retry}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
